/**
 * VenueGalleryUpload component.
 * Allows managing a descriptive image gallery for a venue.
 */

import { useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import { trpc } from '../utils/trpc';
import '../styles/VenueGalleryUpload.css';

interface VenueGalleryUploadProps {
  venueId: string;
}

export default function VenueGalleryUpload({ venueId }: VenueGalleryUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: images, refetch } = trpc.venue.getImages.useQuery({ venue_id: venueId });
  const addImageMutation = trpc.venue.addImage.useMutation({
    onSuccess: () => refetch(),
  });
  const removeImageMutation = trpc.venue.removeImage.useMutation({
    onSuccess: () => refetch(),
  });

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setError(null);

      // 1. Upload Image to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${venueId}/descriptive/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('venues')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Insert into Database via tRPC mutation
      // Calculate sort order (max + 1)
      const maxSortOrder =
        images?.reduce((max, img) => Math.max(max, img.sort_order), -1) ?? -1;

      await addImageMutation.mutateAsync({
        venue_id: venueId,
        storage_path: filePath,
        sort_order: maxSortOrder + 1,
      });
    } catch (err: any) {
      console.error('Gallery upload error:', err);
      setError(err.message || 'Failed to upload image.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async (imageId: number, storagePath: string) => {
    try {
      // 1. Remove from database
      await removeImageMutation.mutateAsync({ image_id: imageId });

      // 2. Optional: Remove from storage
      await supabase.storage.from('venues').remove([storagePath]);
    } catch (err: any) {
      console.error('Remove image error:', err);
      setError('Failed to remove image.');
    }
  };

  // Helper to get public URL for display
  const getPublicUrl = (path: string) => {
    const { data } = supabase.storage.from('venues').getPublicUrl(path);
    return data.publicUrl;
  };

  return (
    <div className="venue-gallery-upload-container">
      <h3 className="gallery-title">Descriptive Images</h3>

      <div className="gallery-grid">
        {images?.map((img) => (
          <div key={img.id} className="gallery-item">
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={getPublicUrl(img.storage_path)}
              alt="Venue"
            />
            <button
              className="remove-image-btn"
              onClick={() => handleRemove(img.id, img.storage_path)}
              aria-label="Remove image"
            >
              &times;
            </button>
          </div>
        ))}

        <div
          className={`gallery-add-card ${isUploading ? 'uploading' : ''}`}
          onClick={handleAddClick}
        >
          {isUploading ? (
            <div className="gallery-spinner"></div>
          ) : (
            <div className="add-content">
              <span className="add-plus">+</span>
              <span className="add-text">Add Image</span>
            </div>
          )}
        </div>
      </div>

      {error && <p className="gallery-error">{error}</p>}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
}
